import { Fragment, useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Route, Routes, useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { getCaptcha, useSlideAnimation, Carousel, Snackbar } from 'sfy-react'
import { postRequestAsync } from '../../common/genericAPIs'
import { setLoader } from '../../redux/common'
import { setErrors, setFields } from '../../redux/login'
import { setConsumerDetails } from '../../redux/user'
import { setSnackbar } from '../../redux/common'
import { setIsRequestCreationInProcess } from '../../redux/request'
import apiInstance from '../../utils/apiInstance'
import { regex, validateValues } from '../../utils/form'
import Otp from './Otp'
import ChangeLanguage from '../../common/components/ChangeLanguage'
import InitialDetails from './InitialDetails'
import NewUserDetails from './NewUserDetails'
import SelectAccount from './SelectAccount'
import styles from './Login.module.scss'
import { defaultLoginBanners, generateRandomString } from '../../utils'
import { setToLocalStorage } from '../../utils/localStorage'
import { consentLabels, getPlaceholders, legalConfigAttributes, replacePlaceholders } from '../../utils/consent'
import poweredByServify from '../../assets/images/powered-by-servify-gray.png'
import { ReactComponent as RightArrow } from '../../assets/images/chevron-right.svg'
import { ReactComponent as LanguageIcon } from '../../assets/images/language-icon.svg'

const LoginLayout = (props) => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()

  const counterInterval = useRef(null)

  const snackbar = useSelector((state) => state.common.snackbar)
  const appConfigDetails = useSelector((state) => state.common.appConfigDetails)
  const selectedCountryDetails = useSelector((state) => state.common.selectedCountryDetails)
  const translatedData = useSelector((state) => state.common.translatedData)
  const consumerDetails = useSelector((state) => state.user.consumerDetails)
  const fields = useSelector((state) => state.login.fields)
  const legalConfigs = useSelector((state) => state.login.legalConfigs)
  const languageCode = useSelector((state) => state.common.languageCode)

  const [isDisabled, setIsDisabled] = useState(true)
  const [counter, setCounter] = useState()
  const [showResend, setShowResend] = useState(false)
  const [sendRecaptcha, setSendRecaptcha] = useState(appConfigDetails.sendCaptcha)
  const [selectedContact, setSelectedContact] = useState('')
  const [selectedAccount, setSelectedAccount] = useState()
  const [selectedAccountValue, setSelectedAccountValue] = useState()

  const animationClassName = useSlideAnimation(location.pathname)

  useEffect(() => {
    if (location.pathname.includes('/login/')) {
      const handleBeforeUnloadEvent = (event) => {
        event.preventDefault()
        event.returnValue = 'Changes that you made may not be saved.'
        return 'Changes that you made may not be saved.'
      }
      const handleOnUnloadEvent = () => {
        localStorage.clear()
      }
      window.addEventListener('beforeunload', handleBeforeUnloadEvent)
      window.addEventListener('unload', handleOnUnloadEvent)
      return () => {
        window.removeEventListener('beforeunload', handleBeforeUnloadEvent)
        window.removeEventListener('unload', handleOnUnloadEvent)
      }
    }
  }, [location])

  useEffect(() => {
    // localStorage.clear()
    return () => {
      clearInterval(counterInterval.current)
    }
  }, [dispatch])

  useEffect(() => {
    setToLocalStorage('languageCode', languageCode)
  }, [languageCode])

  useEffect(() => {
    if (counter === 0) {
      clearInterval(counterInterval.current)
    }
  }, [counter])

  const validate = useCallback(() => {
    dispatch(setErrors({}))
    const formFields = validateValues(fields, {})
    const isValid = formFields.isValid
    return isValid
  }, [fields, dispatch])

  useEffect(() => {
    setIsDisabled(!validate())
  }, [validate])

  const handleOnSSOLogin = () => {
    let callbackURL = ''
    const randomString = generateRandomString(10)
    if (appConfigDetails.callbackURL) {
      callbackURL = appConfigDetails.callbackURL + `state=${randomString}`
    }
    setToLocalStorage('appConfigDetails', appConfigDetails)
    setToLocalStorage('selectedCountryDetails', selectedCountryDetails)
    window.location.replace(callbackURL)
  }

  const handleOnContactChange = (e) => {
    const contact = consumerDetails.existingContact.find((contact) => contact.consumerID === +e.target.value)
    setSelectedContact(contact || e.target.value)
  }

  const setPayload = (isVoiceOTP, noContactFound) => {
    let mobileNumber = fields.mobileNumber
    let email = fields.email
    if (fields.mobileNumberOrPhone) {
      if (regex['mobileNumber'].test(fields.mobileNumberOrPhone)) {
        mobileNumber = fields.mobileNumberOrPhone
      } else {
        email = fields.mobileNumberOrPhone
      }
    }
    let requestObject = {
      mobileNo: mobileNumber,
      emailID: email,
      voiceOtp: isVoiceOTP,
    }
    if (mobileNumber) {
      requestObject.phoneCode = `+${selectedCountryDetails.phoneCode}`
      setSelectedAccount('emailID')
      setSelectedAccountValue(mobileNumber)
    } else {
      setSelectedAccount('mobileNo')
      setSelectedAccountValue(email)
    }
    if (selectedContact.customerReferenceID && !noContactFound) {
      // while email login in mobile verification step send only the customerReferenceID in request object
      requestObject = {}
      requestObject.customerReferenceID = selectedContact.customerReferenceID
    }
    return requestObject
  }

  const handleOnRequestOTP = async (isSMSOTP, isVoiceOTP) => {
    // if (isSMSOTP || isVoiceOTP || validate()) {
    try {
      dispatch(setLoader(true))
      let requestObject = setPayload(isVoiceOTP)
      if (sendRecaptcha) {
        requestObject.captcha = await getCaptcha()
      }
      const response = await postRequestAsync('/consumer/requestOTP', requestObject, 'consumer')
      setCounter(isSMSOTP ? 60 : 30)
      let updatedFields = { otp: '' }
      if (response.sendCaptcha) {
        updatedFields.captcha = ''
      }
      setShowResend(response.allowResend)
      setSendRecaptcha(response.sendCaptcha)
      dispatch(setFields(updatedFields))
      counterInterval.current = setInterval(() => {
        setCounter((counter) => counter - 1)
      }, 1000)
      navigate(searchParams.get('redirect') ? `/login/otp?redirect=${searchParams.get('redirect')}` : '/login/otp')
      dispatch(setLoader(false))
    } catch (e) {
      console.log(e)
      dispatch(setSnackbar({ message: e.msg, type: 'error' }))
      dispatch(setLoader(false))
    }
    // }
  }
  const handleOnVerifyOTP = async (e, apiName) => {
    try {
      let attributes = [{ attributeType: legalConfigAttributes.otp, attributeValue: fields.otp }]
      if (appConfigDetails.enableEmailLogin) {
        attributes.push({ attributeType: legalConfigAttributes.email, attributeValue: fields.email })
      }
      let phs = getPlaceholders(legalConfigs[0].legalConfig.text)
      phs.length &&
        phs.forEach((ph) => {
          attributes.push({
            attributeType: legalConfigAttributes[ph],
            attributeValue: appConfigDetails[consentLabels[ph].value], // getLink(ph)
          })
        })
      dispatch(setLoader(true))
      // conditionally add key values in request object
      // consent array and mobile number fields are not required while email login otp verification
      let requestObject = {
        ...setPayload(null, apiName === 'verifyTokenAndCreateConsumer'),
        OTP: fields.otp,
        ...(apiName !== 'verifyTokenAndCreateConsumer' && {
          consentArray: [
            {
              applicationLegalConfigID: legalConfigs[0].applicationLegalConfigID,
              ...(fields.mobileNumber
                ? { mobileNo: fields.mobileNumber }
                : (regex['mobileNumber'].test(fields.mobileNumberOrPhone) ? fields.mobileNumberOrPhone : '') && {
                    mobileNo:
                      fields.mobileNumber || (regex['mobileNumber'].test(fields.mobileNumberOrPhone) ? fields.mobileNumberOrPhone : ''),
                  }),
              consentText: replacePlaceholders(legalConfigs[0].legalConfig.text, '', translatedData),
              attributes,
            },
          ],
        }),
      }

      if (sendRecaptcha) {
        requestObject.captcha = await getCaptcha()
      }
      const headers = apiName === 'verifyTokenAndCreateConsumer' ? { Authorization: consumerDetails.authorization } : {} // Authorization needs to be passed if validating account
      const response = await postRequestAsync(`/consumer/${apiName}`, requestObject, 'consumer', headers)
      apiInstance.defaults.headers['Authorization'] = response.accessToken && response.accessToken.ID
      response.accessToken && setToLocalStorage('authorization', response.accessToken.ID)
      response.consumerID && setToLocalStorage('consumerID', response.consumerID)
      dispatch(setFields({ otp: '' }))
      dispatch(setConsumerDetails(response))
      dispatch(setLoader(false))
      if (response.existingContact?.length) {
        navigate(searchParams.get('redirect') ? `/login/select-account?redirect=${searchParams.get('redirect')}` : '/login/select-account')
        return
      }
      if (response.isNew) {
        navigate(searchParams.get('redirect') ? `/login/new-user?redirect=${searchParams.get('redirect')}` : '/login/new-user')
        return
      }
      searchParams.get('redirect')?.includes('request') && dispatch(setIsRequestCreationInProcess(true))
      navigate(searchParams.get('redirect') ? searchParams.get('redirect') : appConfigDetails?.afterLoginpage || '/')
    } catch (e) {
      console.log(e)
      dispatch(setSnackbar({ message: e.msg, type: 'error' }))
      dispatch(setLoader(false))
    }
  }

  return (
    <>
      {snackbar.message && (
        <Snackbar
          isRTL={window.isDirectionRTL}
          message={snackbar.message}
          type={snackbar.type}
          callback={() => {
            dispatch(setSnackbar({}))
          }}
          imagePath={require(`../../assets/images/${snackbar.type}-alert.svg`)}
        />
      )}
      <div className={styles['background']}>
        <div className={styles['left-segment']}>
          {
            <Carousel
              items={
                appConfigDetails.loginBackgroundImages &&
                Array.isArray(appConfigDetails.loginBackgroundImages[languageCode.code.toLowerCase()])
                  ? appConfigDetails.loginBackgroundImages[languageCode.code.toLowerCase()]
                  : defaultLoginBanners
              }
              showDots
              showArrows={false}
              className={{
                container: `${styles['carousel-container']}`,
                dotContainer: `${styles['carousel-dots']}`,
                dot: `${styles['carousel-dot']}`,
              }}
              individualBanner={(item, index) => {
                return (
                  <Fragment key={index}>
                    <div
                      style={{
                        backgroundImage: item ? `url(${item.bannerUrl})` : 'var(--fallbackLoginBG)',
                      }}
                      key={index}
                      className={styles['background-img']}
                    />
                    {item.bannerHeaderText && (
                      <div className={styles['banner-text-section']}>
                        <div
                          className={`mb20 ${styles['received-code-text']}`}
                          dangerouslySetInnerHTML={{
                            __html: item.bannerHeaderText,
                          }}
                        />
                        <div
                          className={`${styles['received-code-text']}`}
                          dangerouslySetInnerHTML={{
                            __html: item.bannerSubText,
                          }}
                        />
                      </div>
                    )}
                  </Fragment>
                )
              }}
            />
          }
        </div>
        <div className={styles['right-segment']}>
          <div className={styles['container']}>
            <div className={`${styles['card']} ${animationClassName}`}>
              <div className={styles['login-container-header']}>
                <img className={styles['logo']} src={`/${window.appName}/images/logo.png`} alt='logo' />
                {location.pathname === '/login' && appConfigDetails.showChangeLanguageOption && (
                  <div className={styles['change-language']}>
                    <ChangeLanguage customIcon={<LanguageIcon className={styles['language-icon']} />} />
                    <RightArrow className={styles['language-arrow']} />
                  </div>
                )}
              </div>
              <Routes>
                <Route
                  path='/'
                  element={
                    <InitialDetails handleOnSSOLogin={handleOnSSOLogin} handleOnRequestOTP={handleOnRequestOTP} isDisabled={isDisabled} />
                  }
                />
                <Route
                  path='otp'
                  element={
                    <Otp
                      isDisabled={isDisabled}
                      counter={counter}
                      showResend={showResend}
                      sendRecaptcha={sendRecaptcha}
                      selectedContact={selectedContact}
                      selectedAccount={
                        regex['mobileNumber'].test(fields.mobileNumberOrPhone || fields.mobileNumber)
                          ? `${selectedCountryDetails.phoneCode} - ${fields.mobileNumberOrPhone}`
                          : fields.email || fields.mobileNumberOrPhone
                      }
                      handleOnRequestOTP={handleOnRequestOTP}
                      handleOnVerifyOTP={handleOnVerifyOTP}
                      counterInterval={counterInterval.current}
                    />
                  }
                />
                <Route path='new-user' element={<NewUserDetails showEmail={regex['mobileNumber'].test(fields.mobileNumberOrPhone)} />} />
                <Route
                  path='select-account'
                  element={
                    <SelectAccount
                      handleOnContactChange={handleOnContactChange}
                      handleOnRequestOTP={handleOnRequestOTP}
                      handleOnVerifyOTP={handleOnVerifyOTP}
                      selectedContact={selectedContact}
                      selectedAccount={selectedAccount}
                      selectedAccountValue={selectedAccountValue}
                    />
                  }
                />
              </Routes>
              {(appConfigDetails.footerLinks?.footerText || window.showPServifyText) && (
                <div className='pt20'>
                  {appConfigDetails.footerLinks?.footerText && (
                    <span className='note f12' dangerouslySetInnerHTML={{ __html: appConfigDetails.footerLinks.footerText }} />
                  )}
                  {window.showPServifyText && (
                    <div className='mt10 text-center'>
                      <span className='note f14'>{appConfigDetails.footerLinks?.poweredByText || translatedData.login.powered}</span>
                    </div>
                  )}
                </div>
              )}
              <img src={poweredByServify} alt='powered-by-servify' className={`mt28 ${styles['footer-image']}`} />
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default LoginLayout
